<script setup lang="ts">
import { computed, useTemplateRef } from "vue";
import { UiButton, UiForm, UiFormField, UiInfoBox, UiInputField, UiSelect, UiTextarea } from "@shopexperts/shared";
import { bannedIpFormSchema, type BannedIpFormValues } from "@/app/composables/ban-list/banListForms";

const props = defineProps<{
    isSubmitting?: boolean;
    ruleOptions: readonly { label: string; value: string }[];
    serverErrors?: Record<string, string | undefined>;
}>();

const emit = defineEmits<{
    (e: "submit", values: BannedIpFormValues): void;
    (e: "cancel"): void;
    (e: "change"): void;
}>();

const model = defineModel<BannedIpFormValues>({ required: true });

const formRef = useTemplateRef<{ resetForm: () => void; forceClear: () => void }>("formRef");

const ruleSelectOptions = computed(() => [
    { label: "No rule", value: "" },
    ...props.ruleOptions,
]);

defineExpose({
    resetForm: () => {
        formRef.value?.resetForm();
        formRef.value?.forceClear();
    },
});
</script>

<template>
    <UiForm
        ref="formRef"
        :schema="bannedIpFormSchema"
        :initial-values="model"
        v-model="model"
        :server-errors="props.serverErrors"
        scroll-to-first-error
        @update:model-value="emit('change')"
        @submit="(values: BannedIpFormValues) => emit('submit', values)"
    >
        <div class="flex flex-col gap-5">
            <UiInfoBox variant="info">
                This list is a registry, not a block list — the API never rejects a request because of it. Export these
                addresses to the edge (Cloudflare) to actually block them.
            </UiInfoBox>

            <UiFormField name="ip_address" label="IP address">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiInputField
                        :id="id"
                        name="ip_address"
                        :model-value="modelValue as string"
                        :invalid="invalid"
                        :error="error"
                        :disabled="props.isSubmitting"
                        placeholder="203.0.113.9"
                        hint="IPv4 or IPv6."
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <UiFormField name="email" label="Associated email">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiInputField
                        :id="id"
                        name="email"
                        type="email"
                        :model-value="modelValue as string"
                        :invalid="invalid"
                        :error="error"
                        :disabled="props.isSubmitting"
                        placeholder="spammer@example.com"
                        hint="Optional. Recording it here does not ban the address."
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <UiFormField name="ban_rule_id" label="Attribute to a rule">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiSelect
                        :id="id"
                        name="ban_rule_id"
                        :options="(ruleSelectOptions as { label: string; value: string }[])"
                        :model-value="modelValue as string"
                        :invalid="invalid"
                        :error="error"
                        :disabled="props.isSubmitting"
                        placeholder="No rule"
                        hint="Optional. Groups the address with the rule it belongs to."
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <UiFormField name="notes" label="Notes">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiTextarea
                        :id="id"
                        name="notes"
                        :model-value="modelValue as string"
                        :invalid="invalid"
                        :error="error"
                        :disabled="props.isSubmitting"
                        :rows="3"
                        placeholder="Seen across three spam submissions."
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <div class="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                <UiButton variant="outline" label="Cancel" :disabled="props.isSubmitting" @click="emit('cancel')" />
                <UiButton type="submit" variant="primary" label="Add Address" :disabled="props.isSubmitting" />
            </div>
        </div>
    </UiForm>
</template>
